import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import type { OrganizationWorkflowCreationFailedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowCreationFailed';
import type { OrganizationWorkflowSaveFailedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowSaveFailed';
import SaveOrganizationWorkflow from '@Application/Organizations/Workflows/Commands/SaveOrganizationWorkflow';
import createOrganizationWorkflow from '@Application/Organizations/Workflows/createOrganizationWorkflow';
import saveOrganizationWorkflow from '@Application/Organizations/Workflows/saveOrganizationWorkflow';
import useWorkflowDraft from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Providers/WorkflowDraftProvider/useWorkflowDraft';
import ArchiveDocumentGroupAction from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentGroupEditActions/ArchiveDocumentGroupAction';
import RestoreDocumentGroupAction from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentGroupEditActions/RestoreDocumentGroupAction';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const barStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.625rem;
	flex-wrap: wrap;
`;

type Props = {
	readonly organizationId: number;
	/** Null while the group does not exist yet. */
	readonly workflowKey: string | null;
	readonly workflowLabel: string;
	readonly isActive: boolean;
};

/**
 * Leaf footer of the editor: archive or reactivate (existing group), and save-and-publish. A refused
 * save leaves the server's reason in the draft context (shown above the form) and maps its field
 * errors back onto the draft. Creating leads to the new group's own editor; saving an existing one
 * re-keys the editor on the new version.
 */
const DocumentGroupEditActions: React.FC<Props> = ({
	organizationId,
	workflowKey,
	workflowLabel,
	isActive,
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { draft, setDraft, setRefusalMessage } = useWorkflowDraft();
	const documentTypes = useSelector((state) => state.documentTypesStore.documentTypes);
	const [isSaving, setIsSaving] = React.useState(false);

	const handleSaveClick = React.useCallback(() => {
		if (!draft.isFormDataValid()) {
			const revealed = draft.showErrors();

			setDraft(revealed);
			setRefusalMessage(
				revealed.getValidationMessage('label') ??
					revealed.getValidationMessage('stages') ??
					'Un document est mal réglé : ouvrez ses réglages pour le corriger.',
			);
			return;
		}

		const command = new SaveOrganizationWorkflow(
			organizationId,
			workflowKey,
			draft.toDraft((documentTypeId) => documentTypes?.labelOf(documentTypeId) ?? ''),
		);

		setIsSaving(true);
		setRefusalMessage(null);

		const handleRefusal = (
			action: OrganizationWorkflowSaveFailedAction | OrganizationWorkflowCreationFailedAction,
		) => {
			setRefusalMessage(action.payload?.message ?? 'Le serveur a refusé le groupe.');
			updateValidationFromExternalSource({
				action,
				eventNames: ['ORGANIZATION_WORKFLOW_SAVE_FAILED', 'ORGANIZATION_WORKFLOW_CREATION_FAILED'],
				formDataUpdater: setDraft,
			});
		};

		if (workflowKey === null) {
			dispatch(createOrganizationWorkflow(command))
				.then((action) => {
					if (action.type === 'ORGANIZATION_WORKFLOW_CREATED') {
						navigate(`/administration/document-groups/${action.payload.key}`, { replace: true });
					}
				})
				.catch(handleRefusal)
				.finally(() => setIsSaving(false));
			return;
		}

		dispatch(saveOrganizationWorkflow(command))
			.catch(handleRefusal)
			.finally(() => setIsSaving(false));
	}, [dispatch, navigate, draft, setDraft, setRefusalMessage, organizationId, workflowKey, documentTypes]);

	return (
		<div css={barStyle}>
			<ConditionalLoader data={workflowKey === null}>
				<span />
			</ConditionalLoader>
			<ConditionalLoader data={workflowKey !== null && isActive ? workflowKey : null}>
				{(existingKey) => (
					<ArchiveDocumentGroupAction
						organizationId={organizationId}
						workflowKey={existingKey}
						workflowLabel={workflowLabel}
					/>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={workflowKey !== null && !isActive ? workflowKey : null}>
				{(existingKey) => (
					<RestoreDocumentGroupAction organizationId={organizationId} workflowKey={existingKey} />
				)}
			</ConditionalLoader>
			<Button
				type="button"
				label="Enregistrer et publier"
				loading={isSaving}
				onClick={handleSaveClick}
			/>
		</div>
	);
};

export default DocumentGroupEditActions;
